/**
 * Shared marketing-page primitives.
 *
 * Extracted from BecomeProvider.jsx, BecomeHost.jsx, and About.jsx — these
 * pages each redeclared the same `Eyebrow` chip and `TrustChip` plus the
 * brand palette tokens. Centralising them removes ~50 lines of duplication
 * across the three pages and means a brand-palette tweak is a single-line
 * change instead of three.
 *
 * Each primitive keeps the SAME render output + data-testid format as the
 * inline copies it replaces, so no visual or test regression is possible.
 */
import React from "react";

// Brand palette tokens used across marketing pages.
export const IVORY   = "#FFF8EE";
export const NAVY    = "#071A2D";
export const EMERALD = "#064E3B";
export const GOLD    = "#C9A45C";
export const CORAL   = "#FF7A45";
export const OCEAN   = "#00AEEF";

/**
 * Small uppercase "section eyebrow" used above every marketing section
 * title. Defaults to emerald so existing call sites that omit the color
 * keep rendering exactly as before.
 */
export const Eyebrow = ({ children, color = EMERALD }) => (
  <div
    className="text-[11px] uppercase tracking-[0.24em] font-semibold"
    style={{ color }}
  >
    {children}
  </div>
);

/**
 * Pill-shaped trust indicator used in BecomeProvider/BecomeHost hero rows.
 * Generates `trust-chip-{slug}` data-testids so e2e tests keep working.
 */
export const TrustChip = ({ icon: Icon, label, accent = EMERALD }) => (
  <div
    className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3.5 py-2 border shadow-sm"
    style={{ borderColor: "rgba(7,26,45,0.08)" }}
    data-testid={`trust-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <Icon size={14} style={{ color: accent }} />
    <span className="text-xs font-semibold" style={{ color: NAVY }}>
      {label}
    </span>
  </div>
);
